import { Capacitor } from '@capacitor/core';
import { modifier } from 'ember-modifier';

/**
 * Conditionally applies the "Click to interact" overlay based on a boolean parameter.
 * If `condition` is true, the overlay is NOT applied (map is always interactive).
 * If `condition` is false or undefined, the overlay IS applied.
 * No-op on native Capacitor (Android/iOS) where touch events work natively.
 *
 * @example
 * <div {{mapClickOverlayConditional @alwaysInteractive}}></div>
 */
const mapClickOverlayConditional = modifier(
  (element: HTMLElement, [condition]: [boolean | undefined]) => {
    if (condition || Capacitor.isNativePlatform()) {
      // Map is always interactive, or we're on native (no overlay needed)
      return () => {};
    }

    // Apply the overlay
    const OVERLAY_BASE =
      'absolute inset-0 z-[999] flex items-center justify-center bg-black/25 text-white text-sm font-semibold tracking-wide cursor-pointer transition-opacity duration-200 rounded-[inherit]';
    const OVERLAY_HIDDEN = ['opacity-0', 'pointer-events-none'];

    element.classList.add('relative');

    const overlay = document.createElement('div');
    overlay.className = OVERLAY_BASE;
    overlay.textContent = 'Click to interact';
    element.appendChild(overlay);

    const activate = () => overlay.classList.add(...OVERLAY_HIDDEN);

    element.addEventListener('click', activate);

    return () => {
      element.removeEventListener('click', activate);
      overlay.remove();
    };
  }
);

export default mapClickOverlayConditional;
